/**
 * projectionEngine.ts
 *
 * Mathematical core that converts geographic coordinates (lat/lon) to
 * normalised map coordinates and then to slide coordinates. Implements four
 * projections (Equirectangular, Web Mercator, Robinson, Albers Equal-Area
 * Conic) plus an affine calibration mode for custom/unknown maps.
 *
 * Pure logic — no Office.js, no React.
 */

import type {
  AlbersParams,
  Calibration,
  GeoBounds,
  MapProfile,
  NormalizedPoint,
  ProjectionMapping,
  ReferencePoint,
  ShapeBounds,
} from "../types";

const DEG2RAD = Math.PI / 180;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

interface AlbersCorners {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export class ProjectionEngine {
  /** Cache for Albers corner projections, keyed by profile id. */
  private readonly albersCache = new Map<string, AlbersCorners>();

  // ── Public API ───────────────────────────────────

  /**
   * Project lat/lon to normalised [0,1] coordinates using a map profile.
   * (0,0) = top-left of map, (1,1) = bottom-right. Returns null if the point
   * falls outside the profile bounds (with tolerance).
   */
  projectToNormalized(
    lat: number,
    lon: number,
    profile: MapProfile,
  ): NormalizedPoint | null {
    const PADDING = 0.5; // degrees of tolerance for edge locations

    // Inset handling (e.g. Alaska / Hawaii on a US map).
    if (Array.isArray(profile.insets)) {
      for (const inset of profile.insets) {
        const ib = inset.bounds;
        if (
          lat >= ib.south - PADDING &&
          lat <= ib.north + PADDING &&
          lon >= ib.west - PADDING &&
          lon <= ib.east + PADDING
        ) {
          const norm = this.dispatchProjection(lat, lon, ib, profile);
          if (!norm) return null;
          const vp = inset.viewport;
          return {
            x: vp.x + norm.x * vp.width,
            y: vp.y + norm.y * vp.height,
          };
        }
      }
    }

    // Main bounds check.
    const b = profile.bounds;
    if (
      lat < b.south - PADDING ||
      lat > b.north + PADDING ||
      lon < b.west - PADDING ||
      lon > b.east + PADDING
    ) {
      return null; // outside the map
    }

    const norm = this.dispatchProjection(lat, lon, b, profile);
    if (!norm) return null;

    // Align the projection's normalised output with how this specific SVG
    // actually draws the map (content may be offset/scaled vs the projection
    // frame). Albers maps already fold this into `projectionMapping`, so they
    // don't set `viewBoxCorrection`.
    const c = profile.viewBoxCorrection;
    if (c) {
      return { x: c.sx * norm.x + c.tx, y: c.sy * norm.y + c.ty };
    }
    return norm;
  }

  /** Convert normalised [0,1] coordinates to slide coordinates. */
  normalizedToSlideCoords(
    nx: number,
    ny: number,
    shapeBounds: ShapeBounds,
  ): NormalizedPoint {
    return {
      x: shapeBounds.left + nx * shapeBounds.width,
      y: shapeBounds.top + ny * shapeBounds.height,
    };
  }

  /** Convenience: project lat/lon directly to slide coordinates. */
  projectToSlide(
    lat: number,
    lon: number,
    profile: MapProfile,
    shapeBounds: ShapeBounds,
  ): NormalizedPoint | null {
    const norm = this.projectToNormalized(lat, lon, profile);
    if (!norm) return null;
    return this.normalizedToSlideCoords(norm.x, norm.y, shapeBounds);
  }

  // ── Calibration mode ─────────────────────────────

  /** Compute an affine transform from 2 (scale+translate) or 3 (full) points. */
  calibrate(referencePoints: ReferencePoint[]): Calibration {
    if (!Array.isArray(referencePoints) || referencePoints.length < 2) {
      throw new Error("Need at least 2 reference points for calibration");
    }
    if (referencePoints.length === 2) {
      return this.calibrate2Point(referencePoints);
    }
    return this.calibrate3Point(referencePoints);
  }

  /** Project a coordinate using a previously computed calibration. */
  projectWithCalibration(
    lat: number,
    lon: number,
    calibration: Calibration,
  ): NormalizedPoint {
    if (calibration.type === "2point") {
      return {
        x: calibration.sx * lon + calibration.tx,
        y: calibration.sy * lat + calibration.ty,
      };
    }
    return {
      x: calibration.a * lon + calibration.b * lat + calibration.tx,
      y: calibration.c * lon + calibration.d * lat + calibration.ty,
    };
  }

  // ── Projection implementations ───────────────────

  private dispatchProjection(
    lat: number,
    lon: number,
    bounds: GeoBounds,
    profile: MapProfile,
  ): NormalizedPoint | null {
    switch (profile.projection) {
      case "mercator":
        return this.mercator(lat, lon, bounds);
      case "albers":
        return this.albers(
          lat,
          lon,
          bounds,
          profile.projectionParams ?? null,
          profile.id,
          profile.projectionMapping ?? null,
        );
      case "robinson":
        return this.robinson(lat, lon);
      case "equirectangular":
      default:
        return this.equirectangular(lat, lon, bounds);
    }
  }

  /** Equirectangular (Plate Carrée): linear lon→x, lat→y. */
  private equirectangular(
    lat: number,
    lon: number,
    bounds: GeoBounds,
  ): NormalizedPoint {
    const x = (lon - bounds.west) / (bounds.east - bounds.west);
    const y = (bounds.north - lat) / (bounds.north - bounds.south);
    return { x, y };
  }

  /** Web Mercator. Latitudes clamped to ±85° to avoid divergence. */
  private mercator(
    lat: number,
    lon: number,
    bounds: GeoBounds,
  ): NormalizedPoint {
    const clampedLat = clamp(lat, -85, 85);
    const clampedNorth = clamp(bounds.north, -85, 85);
    const clampedSouth = clamp(bounds.south, -85, 85);

    const x = (lon - bounds.west) / (bounds.east - bounds.west);

    const yPoint = this.mercatorY(clampedLat);
    const yNorth = this.mercatorY(clampedNorth);
    const ySouth = this.mercatorY(clampedSouth);

    // North is larger in Mercator y; invert so that north = 0.
    const y = (yNorth - yPoint) / (yNorth - ySouth);
    return { x, y };
  }

  private mercatorY(latDeg: number): number {
    const latRad = latDeg * DEG2RAD;
    return Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  }

  /**
   * Robinson projection (compromise, world maps). Lookup table at 5° steps.
   * Source: public-domain code by Niklas Bichinger.
   */
  private robinson(lat: number, lon: number): NormalizedPoint {
    const AA = [
      0.8487, 0.84751182, 0.84479598, 0.842013, 0.83359314, 0.8257851,
      0.814752, 0.80006949, 0.78216192, 0.76060494, 0.73658673, 0.7086645,
      0.67777182, 0.64475739, 0.60987582, 0.57134484, 0.52729731, 0.48562614,
      0.45167814,
    ];
    const BB = [
      0.0, 0.0838426, 0.1676852, 0.2515278, 0.3353704, 0.419213, 0.5030556,
      0.5868982, 0.67047034, 0.75336633, 0.83518048, 0.91537187, 0.99339958,
      1.06872269, 1.14066505, 1.20841528, 1.27035062, 1.31998003, 1.3523,
    ];

    const absLat = Math.abs(lat);
    const floor = Math.min(Math.floor(absLat / 5), 17);
    const ceil = floor + 1;
    const frac = (absLat - floor * 5) / 5;

    // Indices are bounded to [0,18] above, so these are always defined.
    const aaFloor = AA[floor]!;
    const aaCeil = AA[ceil]!;
    const bbFloor = BB[floor]!;
    const bbCeil = BB[ceil]!;
    const aa0 = AA[0]!;
    const bb18 = BB[18]!;

    const aa = aaFloor + (aaCeil - aaFloor) * frac;
    const bb = bbFloor + (bbCeil - bbFloor) * frac;

    const robinsonWidth = 2 * Math.PI * aa0;
    const latSign = lat >= 0 ? 1 : -1;

    const relX = (aa * lon * Math.PI) / 180;
    const relY = bb * latSign;

    const widthHeightRatio = (Math.PI * aa0) / bb18;

    const absX = robinsonWidth / 2 + relX;
    const absY = robinsonWidth / widthHeightRatio / 2 - relY;

    const maxX = robinsonWidth;
    const maxY = robinsonWidth / widthHeightRatio;

    return { x: absX / maxX, y: absY / maxY };
  }

  /** Albers Equal-Area Conic (US maps). Corner extents cached per profile id. */
  private albers(
    lat: number,
    lon: number,
    bounds: GeoBounds,
    params: AlbersParams | null,
    profileId: string | undefined,
    projectionMapping: ProjectionMapping | null,
  ): NormalizedPoint | null {
    if (!params) {
      return this.equirectangular(lat, lon, bounds);
    }

    const phi1 = params.standardParallel1 * DEG2RAD;
    const phi2 = params.standardParallel2 * DEG2RAD;
    const lam0 = params.centralMeridian * DEG2RAD;
    const phi0 = params.originLatitude * DEG2RAD;

    const n = 0.5 * (Math.sin(phi1) + Math.sin(phi2));
    if (n === 0) return this.equirectangular(lat, lon, bounds);

    const C = Math.pow(Math.cos(phi1), 2) + 2 * n * Math.sin(phi1);
    const rho0Sq = C - 2 * n * Math.sin(phi0);
    if (rho0Sq < 0) return this.equirectangular(lat, lon, bounds);
    const rho0 = Math.sqrt(rho0Sq) / n;

    const projected = this.albersForward(
      lat * DEG2RAD,
      lon * DEG2RAD,
      n,
      C,
      rho0,
      lam0,
    );
    if (!projected) return null;

    // Preferred: empirically-derived SVG-specific linear mapping.
    if (projectionMapping) {
      return {
        x: projectionMapping.sx * projected.x + projectionMapping.tx,
        y: projectionMapping.sy * projected.y + projectionMapping.ty,
      };
    }

    // Fallback: boundary-based normalisation.
    let cache = profileId != null ? this.albersCache.get(profileId) : undefined;
    if (!cache) {
      cache = this.computeAlbersCorners(bounds, n, C, rho0, lam0);
      if (profileId != null) this.albersCache.set(profileId, cache);
    }

    // X west→east; Y inverted (Albers Y increases north, slide Y increases down).
    const x = (projected.x - cache.minX) / (cache.maxX - cache.minX);
    const y = (cache.maxY - projected.y) / (cache.maxY - cache.minY);
    return { x, y };
  }

  private albersForward(
    phiRad: number,
    lamRad: number,
    n: number,
    C: number,
    rho0: number,
    lam0: number,
  ): NormalizedPoint | null {
    const theta = n * (lamRad - lam0);
    const rhoSq = C - 2 * n * Math.sin(phiRad);
    if (rhoSq < 0) return null;
    const rho = Math.sqrt(rhoSq) / n;
    return {
      x: rho * Math.sin(theta),
      y: rho0 - rho * Math.cos(theta),
    };
  }

  /** Sample all four edges (the conic curves them) to find projected extents. */
  private computeAlbersCorners(
    bounds: GeoBounds,
    n: number,
    C: number,
    rho0: number,
    lam0: number,
  ): AlbersCorners {
    const STEPS = 20;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    const update = (lat: number, lon: number): void => {
      const p = this.albersForward(lat * DEG2RAD, lon * DEG2RAD, n, C, rho0, lam0);
      if (!p) return;
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    };

    for (let i = 0; i <= STEPS; i++) {
      const t = i / STEPS;
      const lon = bounds.west + t * (bounds.east - bounds.west);
      const lat = bounds.south + t * (bounds.north - bounds.south);
      update(bounds.north, lon);
      update(bounds.south, lon);
      update(lat, bounds.west);
      update(lat, bounds.east);
    }

    return { minX, maxX, minY, maxY };
  }

  // ── Calibration implementations ──────────────────

  private calibrate2Point(points: ReferencePoint[]): Calibration {
    const [p1, p2] = points;
    if (!p1 || !p2) throw new Error("2-point calibration needs 2 points");

    const dLon = p2.lon - p1.lon;
    const dLat = p2.lat - p1.lat;
    if (dLon === 0 || dLat === 0) {
      throw new Error(
        "2-point calibration requires points with different lat AND lon values",
      );
    }

    const sx = (p2.slideX - p1.slideX) / dLon;
    const sy = (p2.slideY - p1.slideY) / dLat;
    const tx = p1.slideX - sx * p1.lon;
    const ty = p1.slideY - sy * p1.lat;
    return { type: "2point", sx, sy, tx, ty };
  }

  private calibrate3Point(points: ReferencePoint[]): Calibration {
    const [p1, p2, p3] = points;
    if (!p1 || !p2 || !p3) throw new Error("3-point calibration needs 3 points");

    const det = this.det3(
      [p1.lon, p1.lat, 1],
      [p2.lon, p2.lat, 1],
      [p3.lon, p3.lat, 1],
    );
    if (Math.abs(det) < 1e-12) {
      throw new Error("3-point calibration failed: reference points are collinear");
    }

    const a = this.det3(
      [p1.slideX, p1.lat, 1],
      [p2.slideX, p2.lat, 1],
      [p3.slideX, p3.lat, 1],
    ) / det;
    const b = this.det3(
      [p1.lon, p1.slideX, 1],
      [p2.lon, p2.slideX, 1],
      [p3.lon, p3.slideX, 1],
    ) / det;
    const tx = this.det3(
      [p1.lon, p1.lat, p1.slideX],
      [p2.lon, p2.lat, p2.slideX],
      [p3.lon, p3.lat, p3.slideX],
    ) / det;
    const c = this.det3(
      [p1.slideY, p1.lat, 1],
      [p2.slideY, p2.lat, 1],
      [p3.slideY, p3.lat, 1],
    ) / det;
    const d = this.det3(
      [p1.lon, p1.slideY, 1],
      [p2.lon, p2.slideY, 1],
      [p3.lon, p3.slideY, 1],
    ) / det;
    const ty = this.det3(
      [p1.lon, p1.lat, p1.slideY],
      [p2.lon, p2.lat, p2.slideY],
      [p3.lon, p3.lat, p3.slideY],
    ) / det;

    return { type: "3point", a, b, tx, c, d, ty };
  }

  /** Determinant of a 3×3 matrix given as three row vectors. */
  private det3(
    r1: readonly [number, number, number],
    r2: readonly [number, number, number],
    r3: readonly [number, number, number],
  ): number {
    const [a1, a2, a3] = r1;
    const [b1, b2, b3] = r2;
    const [c1, c2, c3] = r3;
    return (
      a1 * (b2 * c3 - b3 * c2) -
      a2 * (b1 * c3 - b3 * c1) +
      a3 * (b1 * c2 - b2 * c1)
    );
  }
}
